/**
 * The follow-up queue's reads and its build, in one place (issue #147): Oversikt → Oppfølging and
 * Vaktplan's review bar show the same points, so they count the same. The window is the month close
 * (last month and this) and a week ahead; requests are company-wide. Nothing here writes.
 */
import {
  type ApiShift,
  useAbsenceTypes,
  useAbsences,
  usePending,
  useShifts,
  useUsers,
  useWorkTimeChanges,
} from '@/api/data';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { dayEntries, oslo, tenantRules, toPerson } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { DNL, fullName } from '@/ui/format';
import { addDays, dow, mondayOf, weekDays } from '@timefly/core';
import { kindOfAbsence } from '../fravaer/AbsenceRange';
import { closeWindow } from '../timeliste/summary';
import { type DayRow, dayRows, shiftsOfDay } from './day';
import { type Group, type Queue, buildQueue } from './queue';

export interface FollowUp {
  queue: Queue;
  /** a read the queue needs has not arrived yet: not the same as an empty queue */
  loading: boolean;
  /** a read failed, including a failed refresh: the queue is not confirmed complete */
  failed: boolean;
  /** every read failed: nothing is known */
  allFailed: boolean;
  /**
   * Every read the queue uses that has not answered yet or failed (review of PR #150, F4 and RF1),
   * with the queue groups it feeds, so a group is called clear only when all its reads answered.
   */
  unsure: { key: string; label: string; groups: Group[]; state: 'loading' | 'failed' }[];
  /** the dates the queue actually read: the month close's first day to a week ahead (F3) */
  span: { from: string; to: string };
  /** the month-close window's shifts, for cases that need the shift itself */
  closeShifts: ApiShift[];
  ahead: ApiShift[];
  refetch: () => void;
}

/**
 * `todayRows` lets a page that already lists today hand its rows in; otherwise they are read here.
 * `ready` false holds the shift reads back, so a page's own shifts answer first (Vaktplan at BK, 2026-10-28).
 */
export function useFollowUpQueue(today: string, now: number, todayRowsIn?: DayRow[], ready = true): FollowUp {
  const can = useCan();
  const { tenant, mode, workspace } = useTenant();
  const rota = mode === 'rota' && workspace.enabled.scheduling;
  const seesTime = can('timeliste.se');
  const win = closeWindow(today);
  const closeQ = useShifts(win.from, win.to, seesTime && ready);
  const aheadQ = useShifts(addDays(mondayOf(today), -1), addDays(today, 8), ready);
  const todayQ = useShifts(addDays(today, -1), today, !todayRowsIn && ready);
  const usersQ = useUsers();
  const absQ = useAbsences();
  const typesQ = useAbsenceTypes(can('fravaer.se'));
  const pendingQ = usePending(ready);
  const cancelQ = useWorkTimeChanges(
    { action: 'absence_cancel_requested', from: `${addDays(today, -120)}T00:00:00Z` },
    seesTime && ready,
  );
  const tol = tenantRules(tenant).toleranceMin ?? 15;
  const todayRows = todayRowsIn ?? dayRows(shiftsOfDay(todayQ.data?.shifts ?? [], today), now, tol, rota);
  // this week's rule breaches, as Varsler counted them (the company's own rules)
  const rules = tenantRules(tenant);
  const wk = weekDays(mondayOf(today));
  const ahead = aheadQ.data?.shifts ?? [];
  const breaches = tenant
    ? ahead
        .filter((s) => wk.includes(oslo(s.start_time)?.date ?? '') && s.user)
        .map((s) => ({
          s,
          r: dayEntries(s, toPerson(s.user as NonNullable<ApiShift['user']>, tenant), rules).filter(
            (e) => e.sev === 'd' && e.code !== 'open',
          ),
        }))
        .filter((x) => x.r.length)
    : [];
  const types = typesQ.data ?? [];
  const queue = buildQueue({
    today,
    now,
    todayRows,
    ahead,
    closeShifts: closeQ.data?.shifts ?? [],
    users: usersQ.data ?? [],
    absences: absQ.data ?? [],
    kindOf: (a) => kindOfAbsence(a, types),
    cancelRequests: cancelQ.data ?? [],
    pending: pendingQ.isError ? null : (pendingQ.data?.pendingRequests ?? null),
    can: {
      time: seesTime,
      absence: can('fravaer.se'),
      setup: can('ansatte.endre'),
      plan: can('vaktplan.se'),
      planEdit: can('vaktplan.endre'),
    },
    rota,
    tol,
    breaches: breaches.length
      ? {
          count: breaches.length,
          sub: breaches
            .slice(0, 3)
            .map(
              (x) => `${fullName(x.s.user).split(' ')[0]} ${DNL[dow(oslo(x.s.start_time)?.date ?? today)]}`,
            )
            .join(', '),
          monday: mondayOf(today),
        }
      : null,
  });
  // every read the queue uses, by what a manager would miss without it and the groups it feeds; a
  // disabled read is not a gap
  const sources: [
    string,
    string,
    Group[],
    { isError: boolean; data: unknown; isPending: boolean; fetchStatus: string; refetch: () => unknown },
  ][] = [
    ['close', T('timer'), ['timer', 'oppmote'], closeQ],
    ['ahead', T('planen'), ['plan', 'foresporsler'], aheadQ],
    ['today', T('dagens oppmøte'), ['oppmote', 'timer'], todayQ],
    ['users', T('ansatte'), ['oppsett'], usersQ],
    ['absences', T('fravær'), ['foresporsler', 'plan'], absQ],
    ['types', T('fraværstyper'), ['foresporsler', 'plan'], typesQ],
    ['pending', T('forespørsler'), ['foresporsler'], pendingQ],
    ['cancel', T('avlysninger'), ['foresporsler'], cancelQ],
  ];
  const activeSources = sources.filter(
    ([key]) =>
      !(
        (['close', 'cancel'].includes(key) && !seesTime) ||
        (['absences', 'types'].includes(key) && !can('fravaer.se')) ||
        (key === 'today' && todayRowsIn !== undefined)
      ),
  );
  const unsure = activeSources.flatMap(([key, label, groups, q]): FollowUp['unsure'] =>
    q.isError
      ? [{ key, label, groups, state: 'failed' as const }]
      : q.isPending && q.fetchStatus !== 'idle'
        ? [{ key, label, groups, state: 'loading' as const }]
        : [],
  );
  return {
    queue,
    loading: unsure.some((s) => s.state === 'loading'),
    failed: unsure.some((s) => s.state === 'failed'),
    allFailed: activeSources.every(([, , , q]) => q.isError && !q.data),
    unsure,
    span: { from: win.from, to: addDays(today, 8) },
    closeShifts: closeQ.data?.shifts ?? [],
    ahead,
    refetch: () => {
      for (const [, , , q] of activeSources) if (q.isError) void q.refetch();
    },
  };
}
